import merge from 'lodash/merge'
import { configureRefreshFetch } from 'refresh-fetch'

import { LongLiveTokenAPI, oAuthRefresh } from './auth-api'
import Credentials from './Credentials'
import * as api from './gen/api'
import { fetchOrFail, URLbasename } from './helpers'
import { JurisdictionApi } from './jurisdiction-api'
import { clearCredentials, saveCredentials } from './storage'

export interface IAPI {
  agencySettings: api.AgencyApi
  areas: api.ServiceAreasApi
  auditLogs: api.AuditLogsApi
  eventRecords: api.EventRecordsApi
  fees: api.FeesApi
  gbfs: api.GbfsDataApi
  getParkingBayInfo: api.GetParkingBayInfosApi
  graphs: api.GraphsApi
  homepage: api.HomepageApi
  jurisdiction: JurisdictionApi
  mdsApiCredentials: api.MdsCredentialsApi
  offenses: api.OffensesApi
  password: api.PasswordApi
  penaltySchemes: api.PenaltySchemesApi
  policies: api.PoliciesApi
  policyAmounts: api.PolicyAmountsApi
  policyKinds: api.PolicyKindsApi
  polygons: api.PolygonsApi
  powerbi: api.PowerbiApi
  providers: api.ProvidersApi
  reports: api.ReportsApi
  smartPatrol: api.SmartPatrolApi
  stops: api.StopsApi
  toggle2fa: api.UserToggle2faApi
  token: LongLiveTokenAPI
  totp: api.UserTotpApi
  trips: api.TripApi
  uploadParkingBay: api.UploadParkingBayApi
  userinfo: api.UserinfoApi
  users: any
  validate2fa: api.UserValidate2faApi
  vehicles: api.VehiclesApi
}

/** We need a context to access user information across all app */
interface IAuthContext {
  api: IAPI
  credentials: Credentials
  refreshTokenAndSave: () => Promise<void>
}

const shouldRefreshToken = (response: any) => {
  return response.status === 401
}

/**
 */
function buildAuthApi(): IAuthContext {
  const credentials = new Credentials()

  const logoutUser = () => {
    clearCredentials()
    window.location.href = '/'
  }

  const refreshTokenAndSave = async () => {
    try {
      const resp = await oAuthRefresh(credentials.getRefreshToken())
      credentials.setAccessToken(resp.access_token)
      credentials.setRefreshToken(resp.refresh_token)
      saveCredentials({ refreshToken: resp.refresh_token })
    } catch (error) {
      logoutUser()
      throw error
    }
  }

  const overriddenFetch = configureRefreshFetch({
    fetch: (url: string, options = {}) => {
      const accessToken = credentials.getAccessToken()

      let optionsWithToken = options
      if (accessToken) {
        optionsWithToken = merge({}, options, {
          headers: {
            Authorization: `bearer ${accessToken}`,
          },
        })
      }
      return fetchOrFail(url, optionsWithToken)
    },
    refreshToken: refreshTokenAndSave,
    shouldRefreshToken: shouldRefreshToken,
  })

  const basePath = `${URLbasename}/prv`
  const authBasePath = `${basePath}/authent`
  return {
    api: {
      auditLogs: new api.AuditLogsApi({}, basePath, overriddenFetch),
      agencySettings: new api.AgencyApi({}, basePath, overriddenFetch),
      areas: new api.ServiceAreasApi({}, basePath, overriddenFetch),
      eventRecords: new api.EventRecordsApi({}, basePath, overriddenFetch),
      fees: new api.FeesApi({}, basePath, overriddenFetch),
      gbfs: new api.GbfsDataApi({}, basePath, overriddenFetch),
      graphs: new api.GraphsApi({}, basePath, overriddenFetch),
      homepage: new api.HomepageApi({}, basePath, overriddenFetch),
      jurisdiction: new JurisdictionApi({}, basePath, overriddenFetch),
      mdsApiCredentials: new api.MdsCredentialsApi({}, basePath, overriddenFetch),
      offenses: new api.OffensesApi({}, basePath, overriddenFetch),
      password: new api.PasswordApi({}, basePath, overriddenFetch),
      penaltySchemes: new api.PenaltySchemesApi({}, basePath, overriddenFetch),
      policies: new api.PoliciesApi({}, basePath, overriddenFetch),
      policyAmounts: new api.PolicyAmountsApi({}, basePath, overriddenFetch),
      policyKinds: new api.PolicyKindsApi({}, basePath, overriddenFetch),
      polygons: new api.PolygonsApi({}, basePath, overriddenFetch),
      powerbi: new api.PowerbiApi({}, basePath, overriddenFetch),
      providers: new api.ProvidersApi({}, basePath, overriddenFetch),
      reports: new api.ReportsApi({}, basePath, overriddenFetch),
      smartPatrol: new api.SmartPatrolApi({}, basePath, overriddenFetch),
      stops: new api.StopsApi({}, basePath, overriddenFetch),
      toggle2fa: new api.UserToggle2faApi({}, basePath, overriddenFetch),
      token: new LongLiveTokenAPI({}, authBasePath, overriddenFetch),
      totp: new api.UserTotpApi({}, basePath, overriddenFetch),
      trips: new api.TripApi({}, basePath, overriddenFetch),
      userinfo: new api.UserinfoApi({}, basePath, overriddenFetch),
      users: {
        logout: logoutUser,
      },
      validate2fa: new api.UserValidate2faApi({}, basePath, overriddenFetch),
      uploadParkingBay: new api.UploadParkingBayApi({}, basePath, overriddenFetch),
      getParkingBayInfo: new api.GetParkingBayInfosApi({}, basePath, overriddenFetch),
      vehicles: new api.VehiclesApi({}, basePath, overriddenFetch),
    },
    credentials,
    refreshTokenAndSave,
  }
}

const authApi = buildAuthApi()

export const refreshTokenAndSave = authApi.refreshTokenAndSave
export const credentials = authApi.credentials

export default authApi.api
